import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange
} from '@angular/core';
import {ExgMapMarkerDirective} from '../directives';
import {ExgMapInfoWindowManagerService} from '../services';

let infoWindowId = 0;

@Component({
  selector: 'exg-map-info-window',
  template: `
    <div class='exg-map-info-window-content'>
      <ng-content></ng-content>
    </div>
  `
})
export class ExgMapInfoWindowComponent implements OnDestroy, OnChanges, OnInit {
  private static _infoWindowOptionsInputs: string[] = ['disableAutoPan', 'maxWidth'];
  /**
   * The latitude position of the info window (only usefull if you use it ouside of a {@link ExgMapMarkerDirective}).
   */
  @Input() latitude!: number;

  /**
   * The longitude position of the info window (only usefull if you use it ouside of a {@link ExgMapMarkerDirective}).
   */
  @Input() longitude!: number;

  /**
   * Disable auto-pan on open. By default, the info window will pan the map so that it is fully
   * visible when it opens.
   */
  @Input() disableAutoPan!: boolean;

  /**
   * All InfoWindows are displayed on the map in order of their zIndex, with higher values
   * displaying in front of InfoWindows with lower values. By default, InfoWindows are displayed
   * according to their latitude, with InfoWindows of lower latitudes appearing in front of
   * InfoWindows at higher latitudes. InfoWindows are always displayed in front of markers.
   */
  @Input() zIndex!: number;

  /**
   * Maximum width of the infowindow, regardless of content's width. This value is only considered
   * if it is set before a call to open. To change the maximum width when changing content, call
   * close, update maxWidth, and then open.
   */
  @Input() maxWidth!: number;

  /**
   * Holds the marker that is the host of the info window (if available)
   */
  hostMarker!: ExgMapMarkerDirective;

  /**
   * Holds the native element that is used for the info window content.
   */
  content!: Node;

  /**
   * Sets the open state for the InfoWindow. You can also call the open() and close() methods.
   */
  private _isOpen = false;
  @Input()
  set isOpen(value: boolean) {
    this._isOpen = value;
    setTimeout(() => {
      this.isOpenChange.emit(value);
    });


  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  @Input() exclusive = false;
  @Input() togglemode = false;

  /**
   * Emits an event when the info window is closed.
   */
  @Output() infoWindowClose: EventEmitter<void> = new EventEmitter<void>();

  @Output() isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  private _infoWindowAddedToManager = false;
  private _id: string = (infoWindowId++).toString();


  constructor(private _infoWindowManager: ExgMapInfoWindowManagerService, private _el: ElementRef) {
  }

  ngOnInit(): void {
    this.content = this._el.nativeElement.querySelector('.exg-map-info-window-content');
    this._infoWindowManager.addInfoWindow(this);
    this._infoWindowAddedToManager = true;
    this._updateOpenState();
    this._registerEventListeners();
  }

  /** @internal */
  ngOnChanges(changes: { [key: string]: SimpleChange }): void {
    if (!this._infoWindowAddedToManager) {
      return;
    }
    // tslint:disable: no-string-literal
    if ((changes['latitude'] || changes['longitude']) && typeof this.latitude === 'number' &&
      typeof this.longitude === 'number') {
      this._infoWindowManager.setPosition(this);
    }
    if (changes['zIndex']) {
      this._infoWindowManager.setZIndex(this);
    }
    if (changes['isOpen']) {
      this._updateOpenState();
    }
    this._setInfoWindowOptions(changes);
  }

  // tslint:enable: no-string-literal

  private _registerEventListeners(): void {
    this._infoWindowManager.createEventObservable('closeclick', this).subscribe(() => {
      this.isOpen = false;
      this.infoWindowClose.emit();
    });
  }

  private _updateOpenState(): void {
    this.isOpen ? this.open(true) : this.close(true);
  }

  private _setInfoWindowOptions(changes: { [key: string]: SimpleChange }): void {
    const options: { [propName: string]: any } = {};
    const optionKeys = Object.keys(changes).filter(
      k => ExgMapInfoWindowComponent._infoWindowOptionsInputs.indexOf(k) !== -1);
    optionKeys.forEach((k) => {
      options[k] = changes[k].currentValue;
    });
    this._infoWindowManager.setOptions(this, options);
  }

  /**
   * Opens the info window.
   */
  open(update?: boolean): Promise<void> {
    if (this.exclusive) {
      this._infoWindowManager.closeAll(this);
    }
    if (this.togglemode && !update &&  this.isOpen) {
      return this.close();

    }

    if (!update) {
      this.isOpen = true;
    }

    return this._infoWindowManager.open(this);
  }

  /**
   * Closes the info window.
   */
  close(update?: boolean): Promise<void> {
    if (!update) {
      this.isOpen = false;
    }
    return this._infoWindowManager.close(this).then(() => {
      this.infoWindowClose.emit();
    });
  }

  /** @internal */
  id(): string {
    return this._id;
  }

  /** @internal */
  toString(): string {
    return 'ExgMapInfoWindow-' + this._id.toString();
  }

  /** @internal */
  ngOnDestroy(): void {
    this._infoWindowManager.deleteInfoWindow(this);
  }
}
